import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getMagazijnLabel } from "@/types/inventory";

export interface StockAlertProduct {
  id: string;
  product_name: string;
  product_sku: string | null;
  product_bc: string | null;
  product_quantity: number | null;
  current_stock: number;
  magazijn: string;
  magazijn_label: string;
  locatie: string;
  eenheid: string;
  moq: number | null;
  last_check: string;
  check_count: number;
}

export const useStockAlertDetails = (notificationId: string | undefined) => {
  return useQuery({
    queryKey: ['stock-alert-details', notificationId],
    queryFn: async () => {
      if (!notificationId) return [];

      // Fetch monitoring records
      const { data: monitoringData, error: monitoringError } = await supabase
        .from('notification_product_monitoring')
        .select(`
          id,
          product_name,
          product_sku,
          product_bc,
          product_quantity
        `)
        .eq('notification_id', notificationId)
        .eq('is_monitored', true);

      if (monitoringError) throw monitoringError;
      if (!monitoringData || monitoringData.length === 0) return [];

      // Fetch all SKUs that we need to look up
      const skus = monitoringData
        .map(m => m.product_sku)
        .filter((sku): sku is string => sku !== null);
      
      const barcodes = monitoringData
        .filter(m => !m.product_sku && m.product_bc)
        .map(m => m.product_bc)
        .filter((bc): bc is string => bc !== null);

      // Fetch current stock data from voorraad_live in bulk
      let voorraadBySku = new Map<string, any>();
      let voorraadByBc = new Map<string, any>();

      if (skus.length > 0) {
        const { data: voorraadData } = await supabase
          .from('voorraad_live')
          .select('*')
          .in('product_id', skus);
        
        // Group by product_id, keeping the one with highest stock
        voorraadData?.forEach(item => {
          const existing = voorraadBySku.get(item.product_id);
          if (!existing || (item.voorraad || 0) > (existing.voorraad || 0)) {
            voorraadBySku.set(item.product_id, item);
          }
        });
      }

      if (barcodes.length > 0) {
        const { data: voorraadData } = await supabase
          .from('voorraad_live')
          .select('*')
          .in('bc', barcodes);
        
        // Group by bc, keeping the one with highest stock
        voorraadData?.forEach(item => {
          const existing = voorraadByBc.get(item.bc);
          if (!existing || (item.voorraad || 0) > (existing.voorraad || 0)) {
            voorraadByBc.set(item.bc, item);
          }
        });
      }

      // Build products list - filter for those with stock > 0
      const products: StockAlertProduct[] = [];
      
      for (const monitoring of monitoringData) {
        // Get voorraad data by SKU first, then by barcode
        let voorraadData = null;
        if (monitoring.product_sku) {
          voorraadData = voorraadBySku.get(monitoring.product_sku);
        } else if (monitoring.product_bc) {
          voorraadData = voorraadByBc.get(monitoring.product_bc);
        }

        const currentStock = voorraadData?.voorraad ?? 0;
        
        // Only include products that are in stock
        if (currentStock <= 0) continue;

        const magazijnNumber = voorraadData?.magazijn ? parseInt(voorraadData.magazijn) : 0;
        
        products.push({
          id: monitoring.id,
          product_name: monitoring.product_name,
          product_sku: monitoring.product_sku,
          product_bc: monitoring.product_bc,
          product_quantity: monitoring.product_quantity,
          current_stock: currentStock,
          magazijn: voorraadData?.magazijn || '-',
          magazijn_label: magazijnNumber ? getMagazijnLabel(magazijnNumber) : '-',
          locatie: voorraadData?.locatie?.trim() || '-',
          eenheid: voorraadData?.eenheid?.trim() || '-',
          moq: voorraadData?.moq || null,
          last_check: voorraadData?.timestamp_sync || new Date().toISOString(),
          check_count: 1
        });
      }

      // Sort by stock amount descending
      products.sort((a, b) => b.current_stock - a.current_stock);

      return products;
    },
    enabled: !!notificationId,
    staleTime: 30000, // 30 seconds
  });
};
